/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APITariffResponseInterface,
  RequestTariffInterface,
  TariffInitialProperties,
  TariffInterface,
} from '../../interface/tariff.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import tariffApi from '../../utils/api/tariff';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Fetch all tariffs
 */
export const fetchAllTariffs = createAsyncThunk<APITariffResponseInterface, RequestTariffInterface>(
  'tariff-management/fetchAllTariffs',
  async (data: RequestTariffInterface, { dispatch }) => {
    const response = await tariffApi
      .fetchTariffs(data)
      .then((response1: any) => response1)
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        );
      });

    return {
      ...response.data,
      results: response.data.results.map((tariff: TariffInterface) => ({
        ...tariff,
        id: tariff._id,
      })),
    };
  },
);

// a thunk to refresh tariffs
export const refreshAllTariffs = createAsyncThunk<APITariffResponseInterface, RequestTariffInterface>(
  'tariff-management/refreshAllTariffs',
  async (data: RequestTariffInterface) => {
    const response = await tariffApi.fetchTariffs(data);
    return {
      ...response.data,
      results: response.data.results.map((tariff: TariffInterface) => ({
        ...tariff,
        id: tariff._id,
      })),
    };
  },
);


/**
 * Fetch tariff filters
 */
export const fetchTariffFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'tariff/fetchTariffFilters',
  async (__, { dispatch }) => {
    try {
      const response = await tariffApi.fetchTariffFilters();
      return ConfigureListFilterResponse(response.data);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);
/**
 * Fetch tariff counts
 */
export const fetchTariffCounts = createAsyncThunk<any, string>(
  'tariff-management/fetchTariffCounts',
  async (type: string, { dispatch }) => {
    try {
      const response = await tariffApi.fetchTariffCounts(type);
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state
 */
const initialState: TariffInitialProperties = {
  tariffs: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  tariffCounts: {},
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const tariffSlice = createSlice({
  name: 'tariff',
  initialState,
  reducers: {
    /**
     * Update tariff filters
     * @param {any} state - The state
     * @param {any} action - The action
     */
    updateTariffFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };

      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );

        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * Disable filter updated
     * @param {any} state - The state
     */
    disableTariffFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },

    /**
     * Set sorting
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setSortData: (state, action: PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },

  /**
   * Extra reducers
   * @param {any} builder - The builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<TariffInitialProperties>) => {
    builder
      .addCase(fetchAllTariffs.pending, (state) => {
        state.loading = true;
      })

      .addCase(
        fetchAllTariffs.fulfilled,
        (state, action: PayloadAction<APITariffResponseInterface>) => {
          state.loading = false;
          state.tariffs = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllTariffs.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(
      refreshAllTariffs.fulfilled,
      (state, action: PayloadAction<APITariffResponseInterface>) => {
        state.tariffs = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
    builder
      .addCase(fetchTariffFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchTariffFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchTariffFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
    builder
      .addCase(fetchTariffCounts.pending, () => {})
      .addCase(fetchTariffCounts.fulfilled, (state, action: PayloadAction<any>) => {
        state.tariffCounts = action.payload;
      })
      .addCase(fetchTariffCounts.rejected, () => {});
  },
});

export const { updateTariffFilters, disableTariffFilterApplied, setSortData } = tariffSlice.actions;

export default tariffSlice.reducer;
